import { paymentLabel } from '../inbox/inboxRules'
import { exVat } from '../../core/rules/revenue'
import { isVoucherRefund } from '../../core/rules/orderRefunds'
import type { AdminOrder, AdminOrderStatusFilter, AdminOrderSummary, OrderChannel, OrderEventKind, OrderFulfilment, RefundLine } from '../../core/types/adminRequests'

/** Het nette bestelnummer, of "—" zolang het er nog niet is (vóór betalen). */
export const orderNumber = (o: Pick<AdminOrderSummary, 'number'>) => o.number ?? '—'

const fulfilmentLabel: Record<OrderFulfilment, string> = { nieuw: 'Nieuw', ingepakt: 'Ingepakt', verzonden: 'Verzonden', bezorgd: 'Bezorgd' }

// Nooit alleen "Abonnement" (docs/KASSA-ABONNEMENTEN.md §1): een bestelling uit het Bewust Abonnement is een bijvuldoos.
export const channelLabel: Record<OrderChannel, string> = { webshop: 'Webshop', abonnement: 'Bijvullen' }

export const eventLabel: Record<OrderEventKind, string> = {
  geplaatst: 'Geplaatst',
  betaald: 'Betaald',
  ingepakt: 'Ingepakt',
  verzonden: 'Verzonden',
  bezorgd: 'Bezorgd',
  terugbetaald: 'Terugbetaald',
  geannuleerd: 'Geannuleerd',
}

type Tone = 'accent' | 'soft' | 'neutral' | 'success'

/**
 * Eén label per bestelling, in deze volgorde: geannuleerd, niet betaald, helemaal terugbetaald,
 * anders de stand van verzenden. Zelfde kleuren als de inbox.
 */
export function orderBadge(o: AdminOrderSummary): { label: string; tone: Tone } {
  if (o.cancelledAt) return { label: 'Geannuleerd', tone: 'neutral' }
  if (o.paymentStatus !== 'betaald') return { label: paymentLabel[o.paymentStatus], tone: 'neutral' }
  if (o.refundedCents > 0 && o.refundedCents >= o.totalCents) return { label: 'Terugbetaald', tone: 'neutral' }
  const tone: Record<OrderFulfilment, Tone> = { nieuw: 'accent', ingepakt: 'soft', verzonden: 'neutral', bezorgd: 'success' }
  return { label: fulfilmentLabel[o.fulfilment], tone: tone[o.fulfilment] }
}

/** De filters in de lijst, in de volgorde waarin Sarah ze nodig heeft. */
export const statusFilters: { value: AdminOrderStatusFilter; label: string }[] = [
  { value: 'nieuw', label: 'Nieuw (betaald)' },
  { value: 'ingepakt', label: 'Ingepakt' },
  { value: 'verzonden', label: 'Verzonden' },
  { value: 'bezorgd', label: 'Bezorgd' },
  { value: 'open', label: 'Wacht op betaling' },
  { value: 'mislukt', label: 'Betaling mislukt' },
  { value: 'verlopen', label: 'Betaling verlopen' },
  { value: 'terugbetaald', label: 'Met terugbetaling' },
  { value: 'geannuleerd', label: 'Geannuleerd' },
]

export const asStatusFilter = (v: string | null): AdminOrderStatusFilter | undefined =>
  statusFilters.find((f) => f.value === v)?.value
export const asChannel = (v: string | null): OrderChannel | undefined => (v === 'webshop' || v === 'abonnement' ? v : undefined)

/** Hoeveel er per product al terugbetaald is (in stuks), uit eerdere terugbetalingen met regels. */
export function refundedQty(order: AdminOrder): Record<string, number> {
  const out: Record<string, number> = {}
  order.refunds.forEach((r) => r.lines?.forEach((l) => (out[l.productId] = (out[l.productId] ?? 0) + l.qty)))
  return out
}

/** Het bondeel van een annulering staat als "Terug op de cadeaubon", niet als bankterugbetaling. */
export const refundLabel = (r: { reason: string }) => (isVoucherRefund(r) ? 'Terug op de cadeaubon' : 'Terugbetaald')

/**
 * Wat een terugbetaling uniek maakt, voor `useRequestId`. Zelfde vingerafdruk = zelfde `requestId`:
 * opnieuw proberen na een fout (ook 504 `mollie_uncertain`) betaalt dan nooit twee keer uit.
 */
export const refundAttempt = (mode: 'all' | 'lines', amountCents: number, lines: RefundLine[], restock: boolean) =>
  `${mode}|${amountCents}|${JSON.stringify(lines)}|${restock}`

/**
 * Wat het formulier doet na een mislukte terugbetaling. Bij `mollie_uncertain` weet niemand of het
 * geld al onderweg is: niet herladen (een gewijzigd openstaand bedrag zou een nieuwe `requestId`
 * geven) en de bevestiging open laten, zodat dezelfde knop het met dezelfde id opnieuw probeert.
 */
export const afterRefundFailure = (code: string | undefined) =>
  code === 'mollie_uncertain' ? { reload: false, keepConfirming: true } : { reload: true, keepConfirming: false }

/** Het bedrag van gekozen regels, met de prijs zoals betaald. */
export const linesCents = (order: AdminOrder, lines: RefundLine[]) =>
  lines.reduce((s, l) => s + (order.lines.find((x) => x.productId === l.productId)?.unitPriceCents ?? 0) * l.qty, 0)

/** Btw per tarief, voor onder de regels en op de factuur. Gebruikt het tarief dat bij de verkoop is bewaard. */
export function vatBreakdown(order: AdminOrder): { vatRate: number; vatCents: number }[] {
  const byRate = new Map<number, number>()
  const add = (rate: number, gross: number) => byRate.set(rate, (byRate.get(rate) ?? 0) + gross)
  order.lines.forEach((l) => add(l.vatRate, l.lineTotalCents))
  if (order.shippingCents > 0) add(order.shippingVatRate, order.shippingCents)
  return [...byRate]
    .filter(([rate]) => rate > 0)
    .map(([vatRate, gross]) => ({ vatRate, vatCents: gross - exVat(gross, vatRate) }))
    .sort((a, b) => b.vatRate - a.vatRate)
}
